import { SnackbarInterface } from "@/utils/utils";
import {
  FamilyHistory,
  MedicalHistory,
  Patientallergy,
  PatientallergySeverityEnum,
  PatientQuestionAnswer,
  QuestionAnswerTypeEnum,
  SurgicalHistory,
} from "@/services";
import {
  AllergyRow,
  ChooseOneValue,
  GroupValue,
  HistoryRow,
  RowTextValue,
  SocialHistoryQuestion,
  VitalRowDef,
} from "./readmission-f2f-types";
import {
  BLOOD_PRESSURE_FORMAT_MESSAGE,
  BLOOD_PRESSURE_ORDER_MESSAGE,
  DIASTOLIC_RANGE,
  DIASTOLIC_RANGE_MESSAGE,
  SYSTOLIC_RANGE,
  SYSTOLIC_RANGE_MESSAGE,
  VITAL_CHECK_VALUE_MESSAGE,
  VITAL_ROWS,
} from "./readmission-f2f-vitals-constant";

export type SocialStore = Record<string, GroupValue | ChooseOneValue>;

/** Build the social-history save payload rows, keeping existing row uuids for updates. */
export const buildSocialAnswers = (
  questions: SocialHistoryQuestion[],
  store: SocialStore,
  savedUuids: Map<string, string | undefined>,
  patientId?: string
): PatientQuestionAnswer[] => {
  const answers: PatientQuestionAnswer[] = [];
  questions.forEach((q) => {
    const value = store[q.uuid];
    if (!value) return;
    let answer = "";
    let notes: string | undefined;
    if (q.answerType === QuestionAnswerTypeEnum.Text) {
      answer = (value as ChooseOneValue).freeText.trim();
    } else if (!isGroupValue(value)) {
      answer = value.selected;
      notes = value.freeText.trim() || undefined;
    } else {
      answer = value.selected.join(", ");
      notes = value.freeText.trim() || undefined;
    }
    const savedUuid = savedUuids.get(q.uuid);
    if (!answer && !notes && !savedUuid) return;
    answers.push({
      uuid: savedUuid,
      question: { uuid: q.uuid },
      answer,
      notes,
      patient: { [patientId ?? ""]: "" },
    });
  });
  return answers;
};

/** Falls back when `crypto.randomUUID` is missing, as on non-secure (http) origins. */
export const newId = (seed = ""): string =>
  globalThis.crypto?.randomUUID?.() ?? `${seed}-${performance.now()}`;

export const emptyGroupValue = (): GroupValue => ({
  selected: [],
  details: {},
  freeText: "",
});

export const emptyChooseOneValue = (): ChooseOneValue => ({
  selected: "",
  freeText: "",
  detail: "",
});

export const isGroupValue = (
  value: GroupValue | ChooseOneValue | undefined
): value is GroupValue =>
  !!value && Array.isArray((value as GroupValue).selected);

/** Toggle an option in a multi-select group, honouring exclusive clears both ways. */
export const toggleGroupOption = (
  value: GroupValue,
  option: string,
  exclusive?: Record<string, string[]>
): GroupValue => {
  const isSelected = value.selected.includes(option);
  if (isSelected) {
    const details = { ...value.details };
    delete details[option];
    return {
      ...value,
      selected: value.selected.filter((it) => it !== option),
      details,
    };
  }
  const conflicts = new Set<string>(exclusive?.[option] ?? []);
  value.selected.forEach((sel) => {
    if (exclusive?.[sel]?.includes(option)) conflicts.add(sel);
  });
  const details = { ...value.details };
  conflicts.forEach((it) => delete details[it]);
  return {
    ...value,
    selected: [...value.selected.filter((it) => !conflicts.has(it)), option],
    details,
  };
};

/** Rows that clash with `key` in either direction of the exclusive map. */
export const getConflictingRowKeys = (
  key: string,
  exclusive?: Record<string, string[]>
): string[] => [
  ...(exclusive?.[key] ?? []),
  ...Object.keys(exclusive ?? {}).filter((other) =>
    exclusive?.[other]?.includes(key)
  ),
];

/** Rows locked because a checked row excludes them. */
export const getBlockedRowKeys = (
  value: RowTextValue,
  exclusive?: Record<string, string[]>
): Set<string> =>
  new Set(
    Object.entries(exclusive ?? {})
      .filter(([key]) => value[key]?.checked)
      .flatMap(([, others]) => others)
  );

export const sanitizeVitalInput = (def: VitalRowDef, raw: string): string => {
  if (def.format === "bloodPressure") {
    const [systolic, ...rest] = raw.replace(/[^\d/]/g, "").split("/");
    if (!rest.length) return systolic.slice(0, 3);
    return `${systolic.slice(0, 3)}/${rest[0].slice(0, 3)}`;
  }
  const decimals = def.decimals ?? 0;
  const cleaned = raw.replace(decimals ? /[^\d.]/g : /\D/g, "");
  const [whole, ...fraction] = cleaned.split(".");
  const limited = fraction.length
    ? `${whole}.${fraction.join("").slice(0, decimals)}`
    : whole;
  return limited.slice(0, def.maxLength);
};

export const getVitalError = (def: VitalRowDef, value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (def.format === "bloodPressure") {
    const match = /^(\d{2,3})\/(\d{2,3})$/.exec(trimmed);
    if (!match) return BLOOD_PRESSURE_FORMAT_MESSAGE;
    const systolic = Number(match[1]);
    const diastolic = Number(match[2]);
    if (systolic < SYSTOLIC_RANGE.min || systolic > SYSTOLIC_RANGE.max)
      return SYSTOLIC_RANGE_MESSAGE;
    if (diastolic < DIASTOLIC_RANGE.min || diastolic > DIASTOLIC_RANGE.max)
      return DIASTOLIC_RANGE_MESSAGE;
    if (systolic <= diastolic) return BLOOD_PRESSURE_ORDER_MESSAGE;
    return "";
  }
  const number = Number(trimmed);
  const isOutOfRange =
    isNaN(number) ||
    (def.min !== undefined && number < def.min) ||
    (def.max !== undefined && number > def.max);
  if (!isOutOfRange) return "";
  return def.rangeMessage ?? VITAL_CHECK_VALUE_MESSAGE;
};

const isVitalValid = (key: string, value: string): boolean => {
  const def = VITAL_ROWS.find((row) => row.key === key);
  return !!def && !getVitalError(def, value);
};

export const calculateBmi = (heightCm: string, weightKg: string): string => {
  if (!isVitalValid("height", heightCm) || !isVitalValid("weight", weightKg))
    return "—";
  const h = parseFloat(heightCm);
  const w = parseFloat(weightKg);
  if (!h || !w) return "—";
  const meters = h / 100;
  const bmi = w / (meters * meters);
  if (!isFinite(bmi) || bmi <= 0) return "—";
  return bmi.toFixed(1);
};

export const createAllergyRow = (allergy = ""): AllergyRow => ({
  id: newId(allergy),
  allergy,
  severity: "",
  notes: "",
});

export const mapAllergyToRow = (allergy: Patientallergy): AllergyRow => ({
  id: allergy.uuid ?? newId(allergy.allergy),
  uuid: allergy.uuid,
  allergy: allergy.allergy ?? "",
  severity: (allergy.severity as PatientallergySeverityEnum) ?? "",
  notes: allergy.note ?? "",
});

export const isSameAllergyRow = (a: AllergyRow, b: AllergyRow): boolean =>
  a.allergy === b.allergy && a.severity === b.severity && a.notes === b.notes;

export const createHistoryRow = (name = ""): HistoryRow => ({
  id: newId(name),
  name,
  notes: "",
});

export const isSameHistoryRow = (a: HistoryRow, b: HistoryRow): boolean =>
  a.name === b.name && a.notes === b.notes;

export const mapMedicalHistoryToRow = (item: MedicalHistory): HistoryRow => ({
  id: item.uuid ?? newId(item.conditionName),
  uuid: item.uuid,
  name: item.conditionName ?? "",
  notes: item.note ?? "",
});

export const mapSurgicalHistoryToRow = (item: SurgicalHistory): HistoryRow => ({
  id: item.uuid ?? newId(item.surgeryName),
  uuid: item.uuid,
  name: item.surgeryName ?? "",
  notes: item.note ?? "",
});

export const mapFamilyHistoryToRow = (item: FamilyHistory): HistoryRow => ({
  id: item.uuid ?? newId(item.conditionName),
  uuid: item.uuid,
  name: item.conditionName ?? "",
  notes: item.note ?? "",
});

export const recordChangedSnackbar = (
  recordName: string,
  action: "added" | "removed" | "updated"
): SnackbarInterface => ({
  type: "success",
  message: `${recordName} ${action} successfully`,
});
